<script setup lang="ts">
import HeadingNameEdit from "./HeadingNameEdit.vue";
import {
  isJust,
  isNothing,
  just,
  matchMaybe,
  nothing,
  type Maybe,
} from "../lib/maybe";
import { assertNever } from "../lib/assertNever";
import { useTea } from "../lib/tea";
import { computed, watch, nextTick, useTemplateRef } from "vue";
import {
  chapterPrimaryPath,
  defaultZwChapterHeadingStyle,
} from "../lib/zwConfig";
import { isChapterOpeningScene } from "../lib/chapterOpening";
import {
  chapterTitleFontSizePt,
  chapterTitleTextAlign,
} from "../export/chapterHeading";
import { resolveImagePreviewSrc } from "../editor/imagePreviewUrls";
import {
  useEditorSessionStore,
  type ActiveSceneInfo,
} from "../stores/useEditorSessionStore";
import { useProjectWorkspaceStore } from "../stores/useProjectWorkspaceStore";
import { useDisplayModeStore } from "../stores/useDisplayModeStore";
import {
  initialSceneTitleBarModel,
  updateSceneTitleBarFn,
  type SceneTitleBarCmd,
} from "./SceneTitleBar.update";
import { useChapterHeadings } from "../composables/useChapterHeadings";
import { storedChapterName } from "../lib/chapterNumbering";
import ZwInput from "./ui/ZwInput.vue";

const session = useEditorSessionStore();
const workspace = useProjectWorkspaceStore();
const display = useDisplayModeStore();
const { headingFor, canRenameChapter, chapterRenamePrefix } =
  useChapterHeadings();
const titleInput = useTemplateRef<InstanceType<typeof ZwInput>>("titleInput");
const computedTitle = computed(() =>
  matchMaybe(info.value, {
    just: (active) =>
      matchMaybe(headingFor(active.folder), {
        just: (heading) => heading.lines.join("\n"),
        nothing: () => titleValue.value,
      }),
    nothing: () => titleValue.value,
  }),
);
const renamePrefix = computed(() =>
  matchMaybe(info.value, {
    just: (active) => chapterRenamePrefix(active.folder),
    nothing: () => "",
  }),
);
const hasComputedHeading = computed(
  () =>
    isJust(session.projectConfig) &&
    session.projectConfig.value.chapterNumbering.pattern !== "name",
);
async function beginEdit(): Promise<void> {
  if (titleReadOnly.value) return;
  await dispatch({ type: "BeginEdit" });
  await nextTick();
  titleInput.value?.focus();
}

const trashPreview = computed(() => session.trashPreview);
const trashPreviewActive = computed(() => isJust(trashPreview.value));
const info = computed((): Maybe<ActiveSceneInfo> =>
  trashPreviewActive.value ? nothing : session.activeSceneInfo,
);

/** Always the chapter folder name, whether one scene or many is open. Scene
 * names are not shown or edited here; scene details live in the right sidebar. */
const titleValue = computed(() => {
  if (isJust(trashPreview.value)) return trashPreview.value.value.label;
  return matchMaybe(info.value, {
    nothing: () => "",
    just: (active) => storedChapterName(active.chapter),
  });
});

const nameHidden = computed(() =>
  matchMaybe(info.value, {
    just: (active) => canRenameChapter(active.folder) === false,
    nothing: () => false,
  }),
);
const titleReadOnly = computed(
  () => trashPreviewActive.value || display.isReading || nameHidden.value,
);

/** Styled editable title only on the chapter’s first scene (#264). */
const opensChapter = computed(() =>
  matchMaybe(session.projectConfig, {
    just: (config) =>
      matchMaybe(session.activeBasename, {
        just: (path) => isChapterOpeningScene(config.manuscript, path),
        nothing: () => false,
      }),
    nothing: () => false,
  }),
);

/** Large title chrome: opening scene, or trash preview label. */
const showChapterTitle = computed(
  () => opensChapter.value === true || trashPreviewActive.value === true,
);

/** Styling ▸ Chapter titles, mirrored from export (#85 / #263). */
const chapterHeadingStyle = computed(() =>
  matchMaybe(session.projectConfig, {
    just: (config) => config.chapterHeadingStyle,
    nothing: () => defaultZwChapterHeadingStyle(),
  }),
);

const titleAlign = computed(() =>
  chapterTitleTextAlign(chapterHeadingStyle.value.alignment),
);

const titleFontSize = computed(
  () => `${chapterTitleFontSizePt(chapterHeadingStyle.value.scale)}pt`,
);

const titleBlockStyle = computed(() => ({
  textAlign: titleAlign.value,
  marginTop: `${chapterHeadingStyle.value.spaceBefore}em`,
  marginBottom: `${chapterHeadingStyle.value.spaceAfter}em`,
  "--zw-chapter-title-size": titleFontSize.value,
  "--zw-chapter-title-align": titleAlign.value,
}));

const headingImagePath = computed(() => chapterHeadingStyle.value.image.trim());

const headingImageSrc = computed(() => {
  const path = headingImagePath.value;
  if (path.length === 0) return "";
  return resolveImagePreviewSrc(
    path,
    session.rootPath,
    session.imagePreviewRevision,
  );
});

const showHeadingImage = computed(
  () => headingImageSrc.value.length > 0 && display.isFocus === false,
);

const imageAbove = computed(
  () =>
    showHeadingImage.value === true &&
    chapterHeadingStyle.value.imagePosition === "above",
);

const imageBelow = computed(
  () =>
    showHeadingImage.value === true &&
    chapterHeadingStyle.value.imagePosition === "below",
);

function runTitleCmd(cmd: SceneTitleBarCmd): void {
  switch (cmd.kind) {
    case "rename": {
      if (isNothing(info.value)) return;
      void workspace.onRenameProjectFile(
        chapterPrimaryPath(info.value.value.chapter),
        cmd.title,
      );
      return;
    }
    default:
      assertNever(cmd as never);
  }
}

const { model, dispatch } = useTea(
  initialSceneTitleBarModel,
  updateSceneTitleBarFn,
  { runCmd: just(runTitleCmd) },
);

const titleDraft = computed({
  get: (): string => model.value.titleDraft,
  set: (value: string): void => {
    void dispatch({ type: "DraftChanged", draft: value });
  },
});

watch(
  titleValue,
  (value) => {
    void dispatch({ type: "Seed", title: value });
  },
  { immediate: true },
);

function commitTitle(): void {
  if (titleReadOnly.value) return;
  if (isNothing(info.value)) return;
  void dispatch({ type: "Commit", canonicalTitle: titleValue.value });
}
</script>

<template>
  <div v-if="isJust(info) || trashPreviewActive" class="scene-title-bar">
    <div
      v-if="showChapterTitle"
      class="chapter-title-block"
      :data-alignment="chapterHeadingStyle.alignment"
      :style="titleBlockStyle"
    >
      <img
        v-if="imageAbove"
        class="chapter-title-image"
        :src="headingImageSrc"
        alt=""
      />
      <div class="scene-title-row">
        <div
          v-if="nameHidden"
          class="computed-chapter-title"
          role="heading"
          aria-level="2"
        >
          {{ computedTitle }}
        </div>
        <button
          v-else-if="hasComputedHeading && model.editing === false"
          type="button"
          class="computed-chapter-title"
          aria-label="Edit chapter name"
          :disabled="titleReadOnly"
          @click="beginEdit"
        >
          {{ computedTitle }}
        </button>
        <HeadingNameEdit
          v-else
          :prefix="renamePrefix"
          :stacked="renamePrefix.endsWith('\n')"
          class="scene-title-edit"
        >
          <span class="scene-title-name-field">
            <span class="scene-title-name-size" aria-hidden="true">{{
              titleDraft.length > 0 ? titleDraft : "Untitled chapter"
            }}</span>
            <ZwInput
              :id="nothing"
              ref="titleInput"
              v-model="titleDraft"
              variant="plain"
              class="scene-title-input"
              placeholder="Untitled chapter"
              aria-label="Chapter title"
              :readonly="titleReadOnly"
              :label="nothing"
              @keydown.enter.prevent="commitTitle"
              @blur="commitTitle"
              @keydown.escape.prevent="
                dispatch({ type: 'Seed', title: titleValue })
              "
            />
          </span>
        </HeadingNameEdit>
      </div>
      <img
        v-if="imageBelow"
        class="chapter-title-image"
        :src="headingImageSrc"
        alt=""
      />
    </div>
  </div>
</template>

<style scoped>
.scene-title-bar {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  box-sizing: border-box;
  /* Matches .zw-prose's own box exactly (width, max-width, centering
     margins, and horizontal padding) so the title column lines up
     with, and is never wider than, the manuscript text column below it.
     `width: 100%` is explicit, not left to flex stretch: this element's
     parent (.zw-editor-shell) has other non-flex-flow siblings positioned
     absolutely, and relying on cross-axis stretch alone left this collapsed
     to its content width instead of the full available column. */
  width: 100%;
  max-width: 40rem;
  margin-right: auto;
  margin-left: var(--zw-prose-margin-left, 0px);
  padding: var(--space-xl) 1.5rem var(--space-sm);
}

.chapter-title-block {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.scene-title-row {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 0.2rem;
}

.computed-chapter-title {
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font-family: var(--font-heading);
  font-size: var(--zw-chapter-title-size, 19pt);
  font-weight: 600;
  line-height: 1.25;
  text-align: var(--zw-chapter-title-align, center);
  white-space: pre-line;
  overflow-wrap: anywhere;
  cursor: default;
}
button.computed-chapter-title {
  cursor: text;
}
.scene-title-input {
  --title-font-size: var(--zw-chapter-title-size, 19pt);
  flex: 1;
  min-width: 0;
}

.scene-title-input :deep(.zw-input),
.scene-title-input :deep(.zw-input-face-text) {
  font-family: var(--font-heading);
  font-size: var(--title-font-size);
  font-weight: 600;
  line-height: 1.25;
  text-align: var(--zw-chapter-title-align, center);
}

.chapter-title-image {
  display: block;
  max-width: 40%;
  max-height: 2.5in;
  height: auto;
  margin-bottom: 0.35em;
}

.chapter-title-block[data-alignment="left"] .chapter-title-image {
  margin-left: 0;
  margin-right: auto;
}

.chapter-title-block[data-alignment="center"] .chapter-title-image {
  margin-left: auto;
  margin-right: auto;
}

.chapter-title-block[data-alignment="right"] .chapter-title-image {
  margin-left: auto;
  margin-right: 0;
}

.scene-title-row + .chapter-title-image {
  margin-top: 0.35em;
  margin-bottom: 0;
}

.scene-title-edit {
  font-family: var(--font-heading);
  font-size: var(--zw-chapter-title-size, 19pt);
  font-weight: 600;
  text-align: var(--zw-chapter-title-align, center);
  align-items: center;
  justify-content: center;
  line-height: 1.25;
}
.chapter-title-block[data-alignment="left"] .scene-title-edit {
  justify-content: flex-start;
}
.chapter-title-block[data-alignment="right"] .scene-title-edit {
  justify-content: flex-end;
}
.scene-title-name-field {
  display: grid;
  grid-template: 1fr / minmax(0, 1fr);
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
}
.scene-title-name-size {
  grid-area: 1 / 1;
  visibility: hidden;
  white-space: pre;
  font: inherit;
}
.scene-title-edit :deep(.scene-title-input.zw-input-shell) {
  grid-area: 1 / 1;
  width: 100%;
  min-width: 0;
  font: inherit;
}
.scene-title-edit
  :deep(
    .scene-title-input.zw-input-shell .zw-input-plain[aria-multiline="false"]
  ),
.scene-title-edit
  :deep(
    .scene-title-input.zw-input-shell.zw-input-plain-shell:not(
        .zw-textarea-shell
      )
      .zw-input-face-text
  ) {
  text-align: left;
  padding: 0;
  overflow: hidden;
}
.scene-title-edit.heading-name-edit-stacked .scene-title-name-field {
  align-self: center;
}
.chapter-title-block[data-alignment="left"] .scene-title-name-field {
  align-self: flex-start;
}
.chapter-title-block[data-alignment="right"] .scene-title-name-field {
  align-self: flex-end;
}
</style>
